import { useState } from "react";
import { MapPin, Satellite, ExternalLink } from "lucide-react";
import { usd, fecha, mesCorto } from "./util";

const sup = (i) => (i.urbano ? `${Math.round(i.superficie_m2 || 0).toLocaleString("es-PY")} m²` : `${(i.superficie_ha || 0).toLocaleString("es-PY", { maximumFractionDigits: 1 })} ha`);

const Inmuebles = ({ d }) => {
  const items = d.inmuebles?.items || [];
  if (!items.length) return <p className="rs-vacio">Este cliente no tiene inmuebles cargados.</p>;
  return (
    <div className="rs-grilla" data-testid="resumen-inmuebles-grilla">
      {items.map((i, k) => {
        const coords = i.lat != null && i.lng != null ? `${i.lat},${i.lng}` : null;
        const maps = i.maps_url || (coords ? `https://www.google.com/maps?q=${coords}` : "");
        return (
          <div className="rs-inm" key={k}>
            <b>{i.finca_matricula || i.padron_catastro || "Inmueble"}{i.fuente && <small style={{ marginLeft: 6, fontWeight: 400, opacity: 0.7 }}>{i.fuente_texto || (i.fuente === "microfilm" ? "microfilm" : i.fuente === "whatsapp" ? "ubicación WhatsApp · sin microfilm" : i.fuente === "microfilm+whatsapp" ? "microfilm + ubicación WhatsApp" : "análisis")}</small>}</b>
            <span>{[i.distrito, i.departamento].filter(Boolean).join(", ") || "Ubicación —"}</span>
            <span>{sup(i)} · {i.urbano ? "Urbano" : "Rural"}</span>
            <span>{i.valor_ref ? (i.valor_ref.min != null ? <span className={`rs-est ${i.valor_ref.tipo === "tasacion" ? "al_dia" : "por_vencer"}`}>{i.valor_ref.tipo === "tasacion" ? "Tasación" : "Referencia"} {i.valor_ref.min === i.valor_ref.max ? usd(i.valor_ref.min) : `${usd(i.valor_ref.min)} – ${usd(i.valor_ref.max)}`}</span> : <span className="rs-est" style={{ background: "#3A4737", color: "#F3F7F2" }}>Sin referencia de zona</span>)
              : i.valor_usd ? <span className={`rs-est ${i.valor_origen === "tasacion" ? "al_dia" : "por_vencer"}`}>{i.valor_origen === "tasacion" ? "Tasación" : "Referencia"} {usd(i.valor_usd)}</span> : <span className="rs-est" style={{ background: "#3A4737", color: "#F3F7F2" }}>Sin tasación</span>}</span>
            {(i.hipoteca_gravamen && i.hipoteca_gravamen !== "Libre de gravamen") && <span className="rs-alerta">{i.hipoteca_gravamen}{i.acreedor ? ` · ${i.acreedor}` : ""}</span>}
            {i.alerta_tasacion && i.alerta_tasacion !== "ok" && <span className="rs-alerta">{i.alerta_tasacion}</span>}
            {i.observaciones && <span>{i.observaciones}</span>}
            <div className="pills">
              {coords && <a href={`https://maps.google.com/maps?q=${coords}&t=k&z=14`} target="_blank" rel="noreferrer"><Satellite className="inline h-3 w-3 mr-1" />Ver satélite</a>}
              <a href={i.visor_url || "https://www.catastro.gov.py/visor/"} target="_blank" rel="noreferrer"><ExternalLink className="inline h-3 w-3 mr-1" />Visor SNC</a>
              {maps && <a href={maps} target="_blank" rel="noreferrer"><MapPin className="inline h-3 w-3 mr-1" />Maps</a>}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const Cartera = ({ d }) => {
  const c = d.cartera || {}, s = c.serie || [];
  const max = Math.max(1, ...s.map((x) => x.saldo));
  return (
    <div data-testid="resumen-cartera-detalle">
      {s.length === 0 ? <p className="rs-vacio">Sin períodos importados.</p> : (
        <div className="rs-barras" style={{ gridTemplateColumns: `repeat(${s.length}, 1fr)` }}>
          {s.map((x) => (
            <div className="col" key={x.periodo} title={`${mesCorto(x.periodo)}: saldo ${usd(x.saldo)}, vencido ${usd(x.vencido)}`}>
              <div className="b" style={{ height: `${(x.saldo / max) * 100}%`, minHeight: x.saldo ? 6 : 2 }}><i style={{ height: `${x.saldo ? (x.vencido / x.saldo) * 100 : 0}%` }} /></div>
              <small>{mesCorto(x.periodo)}</small>
            </div>
          ))}
        </div>
      )}
      <div className="rs-minis">
        <div className="rs-mini"><small>% vencido hoy</small><b>{c.pct_vencido === null || c.pct_vencido === undefined ? "—" : `${c.pct_vencido}%`}</b></div>
        <div className="rs-mini"><small>Cobrado 12 meses</small><b>{usd(c.cobrado_12m)}</b></div>
        <div className="rs-mini"><small>Atraso máximo</small><b>{c.atraso_max ? `${c.atraso_max} días` : "—"}</b></div>
        <div className="rs-mini"><small>Tendencia</small><b className={`rs-tend ${c.tendencia}`}>{c.tendencia || "—"}</b></div>
      </div>
      <p className="rs-lectura" data-testid="resumen-cartera-lectura">{c.lectura}</p>
    </div>
  );
};

const Pagares = ({ d }) => {
  const p = d.pagares || {}, items = p.items || [];
  if (!items.length) return <p className="rs-vacio">No tiene pagarés pendientes de firma.</p>;
  return (
    <div data-testid="resumen-pagares-detalle">
      <div className="rs-total" style={{ borderTop: 0, marginTop: 0, paddingTop: 0, marginBottom: 8 }}><span>{p.cantidad} pagaré{p.cantidad > 1 ? "s" : ""}</span><span>{usd(p.total)}</span></div>
      {items.map((x, i) => (
        <div className="rs-pag" key={i}>
          <span><small>Emisión</small>{fecha(x.fecha_emision)}</span>
          <span><small>Vencimiento</small>{fecha(x.fecha_vencimiento)}</span>
          <span className={`rs-dias ${(x.dias ?? 0) >= 20 ? "r" : "a"}`}>{x.dias === null ? "—" : `${x.dias} días sin firma`}</span>
          <span className="m">{usd(x.monto)}</span>
        </div>
      ))}
    </div>
  );
};

const rangoRef = (r) => (!r || !r.valuados ? "sin referencia de zona" : `${r.min === r.max ? usd(r.min) : `${usd(r.min)} – ${usd(r.max)}`}${r.parcial ? ` · parcial ${r.valuados}/${r.de}` : ""}`);

export const Tarjetas3 = ({ d, onAbrirInmuebles }) => {
  const [abierta, setAbierta] = useState(null);
  const s = d.cartera?.serie || [], max = Math.max(1, ...s.map((x) => x.saldo));
  const inm = d.inmuebles || {}, pg = d.pagares || {}, mf = inm.microfilm;
  const nInm = inm.items?.length || 0;
  const cards = [
    { id: "inmuebles", t: "Inmuebles", g: `${nInm} inmueble${nInm === 1 ? "" : "s"}`,
      dsc: mf ? `${(mf.ha || 0).toLocaleString("es-PY", { maximumFractionDigits: 1 })} ha · ${mf.libres} libres / ${mf.hipotecados} hipotecados · ref. ${rangoRef(mf.valor_total)}${mf.veces_deuda != null ? ` · ${mf.veces_deuda}× su deuda` : ""}${inm.fuentes?.analisis ? ` · fuentes: microfilm ${inm.fuentes.microfilm} + análisis ${inm.fuentes.analisis}` : " · fuente: microfilm"}`
        : nInm ? `${(inm.total_ha || 0).toLocaleString("es-PY", { maximumFractionDigits: 1 })} ha · ${usd(inm.valor_total_usd)} · fuente: análisis de cliente` : "Sin inmuebles cargados",
      link: mf && onAbrirInmuebles ? () => onAbrirInmuebles(mf.codigo) : null },
    { id: "cartera", t: "Análisis de cartera", g: d.cartera?.pct_vencido === null || d.cartera?.pct_vencido === undefined ? "—" : `${d.cartera.pct_vencido}% vencido`, dsc: `${s.length} período${s.length === 1 ? "" : "s"} · ${d.cartera?.tendencia || "—"}`, mini: true },
  ];
  const Det = { inmuebles: Inmuebles, cartera: Cartera, pagares: Pagares }[abierta];
  return (
    <>
      <div className="rs-fila3">
        {cards.map((c, i) => (
          <button key={c.id} className="rs-card" style={{ animationDelay: `${i * 80}ms` }} aria-expanded={abierta === c.id} onClick={() => setAbierta(abierta === c.id ? null : c.id)} data-testid={`resumen-card-${c.id}`}>
            <h4>{c.t}</h4><div className="g">{c.g}</div><div className="d">{c.dsc}</div>
            {c.link && <span role="link" className="rs-btn verde" style={{ display: "inline-block", marginTop: 6, fontSize: 11 }} onClick={(e) => { e.stopPropagation(); c.link(); }} data-testid="resumen-ir-inmuebles">Abrir en Inmuebles de clientes →</span>}
            {c.mini && s.length > 0 && <div className="rs-minibar">{s.map((x) => <i key={x.periodo} style={{ height: `${(x.saldo / max) * 100}%` }}><b style={{ height: `${x.saldo ? (x.vencido / x.saldo) * 100 : 0}%` }} /></i>)}</div>}
          </button>
        ))}
      </div>
      {Det && <div className="rs-detalle" data-testid={`resumen-detalle-${abierta}`}><h3>{cards.find((c) => c.id === abierta).t}</h3><Det d={d} /></div>}
    </>
  );
};
